import React, { useState, useRef } from 'react';

// Servo calibration for a heater device. Each change is sent immediately (debounced) — no save button —
// because the backend drives the servo to the position currently selected under the new calibration, so
// the operator sees the effect live as they tune. Lost-motion slack is applied by the backend.
const FIELDS = [
  { key: 'maxAngle', label: 'Áng. máx.' },
  { key: 'midAngle', label: 'Áng. medio' },
  { key: 'minAngle', label: 'Áng. mín.' },
  { key: 'lostMotionDegrees', label: 'Holgura (°)' },
  { key: 'angleMoveDelay', label: 'Demora (ms)' },
];

// Which stop (servo angle) each power level drives to. Stop values are the backend HeaterStop ordinals:
// Low = 0 (min angle), Mid = 1 (mid angle), High = 2 (max angle). The mapping must stay a bijection, so
// picking a stop already taken hands the freed stop to the power level that had it.
const STOPS = [
  { value: 2, label: 'Áng. máx' },
  { value: 1, label: 'Áng. medio' },
  { value: 0, label: 'Áng. mín' },
];
const POWER_STOPS = [
  { key: 'pilotStop', label: 'Piloto', fallback: 2 },
  { key: 'minPowerStop', label: 'Mín', fallback: 1 },
  { key: 'maxPowerStop', label: 'Máx', fallback: 0 },
];

const CalibrationInput = ({ device, onSave }) => {
  const [draft, setDraft] = useState(() => ({
    ...Object.fromEntries(FIELDS.map(f => [f.key, device[f.key]])),
    ...Object.fromEntries(POWER_STOPS.map(p => [p.key, device[p.key] ?? p.fallback])),
  }));
  const [status, setStatus] = useState(null); // 'sending' | 'sent' | 'error'
  const [errorMsg, setErrorMsg] = useState(null);
  const timer = useRef(null);

  const send = async (body) => {
    setStatus('sending');
    try {
      await onSave(body);
      setStatus('sent');
    } catch (e) {
      setErrorMsg(typeof e.response?.data === 'string' ? e.response.data : null);
      setStatus('error');
    }
  };

  const queue = (next) => {
    setDraft(next);
    setStatus(null);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => send(next), 400);
  };

  const setField = (key, value) => queue({ ...draft, [key]: value === '' ? null : Number(value) });

  const setStop = (key, value) => {
    const previous = draft[key];
    const next = { ...draft, [key]: value };
    for (const { key: other } of POWER_STOPS) {
      if (other !== key && next[other] === value) next[other] = previous; // keep the bijection
    }
    queue(next);
  };

  return (
    <div className="mt-2">
      <div className="row g-2">
        {FIELDS.map(({ key, label }) => (
          <div className="col" key={key}>
            <label className="form-label small mb-1">{label}</label>
            <input
              type="number"
              className="form-control form-control-sm"
              value={draft[key] ?? ''}
              onChange={(e) => setField(key, e.target.value)}
            />
          </div>
        ))}
      </div>
      <div className="row g-2 mt-1">
        {POWER_STOPS.map(({ key, label }) => (
          <div className="col" key={key}>
            <label className="form-label small mb-1">{label}</label>
            <select
              className="form-select form-select-sm"
              value={draft[key]}
              onChange={(e) => setStop(key, Number(e.target.value))}
            >
              {STOPS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        ))}
      </div>
      <div className="small mt-1" style={{ minHeight: '1.2em' }}>
        {status === 'sending' && <span className="text-muted">Enviando…</span>}
        {status === 'sent' && <span className="text-success">Enviado</span>}
        {status === 'error' && <span className="text-danger">{errorMsg || 'No se pudo enviar'}</span>}
      </div>
    </div>
  );
};

export default CalibrationInput;
